import { useState } from "react";
import { useHydrated } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { submitContactMessage } from "@/lib/contact.functions";
import { m } from "@/paraglide/messages.js";

export function ContactForm() {
  const hydrated = useHydrated();
  const [pending, setPending] = useState(false);

  const submitMut = useMutation({
    mutationFn: (data: {
      name: string;
      email: string;
      phone?: string;
      message: string;
      website?: string;
    }) => submitContactMessage({ data }),
    onSuccess: () => {
      toast.success(m.contact_toast_sent());
    },
    // Network/server errors are raw English (e.g. "Failed to fetch"); show a
    // localized generic message instead and keep the detail in the console.
    onError: (e: Error) => {
      console.error("[contact] submit failed:", e.message);
      toast.error(m.contact_toast_error());
    },
  });

  const submitting = pending || submitMut.isPending;

  return (
    <form
      className="relative mt-12 max-w-xl space-y-4"
      // Avoid native GET navigation if submit fires before React hydration.
      method="post"
      onSubmit={(e) => {
        e.preventDefault();
        if (!hydrated || submitting) return;

        const form = e.currentTarget;
        const fd = new FormData(form);
        const name = String(fd.get("name") ?? "").trim();
        const email = String(fd.get("email") ?? "").trim();
        const phone = String(fd.get("phone") ?? "").trim();
        const message = String(fd.get("message") ?? "").trim();
        const website = String(fd.get("website") ?? "");

        if (!name || !email || !message) return;

        setPending(true);
        submitMut.mutate(
          {
            name,
            email,
            phone: phone || undefined,
            message,
            website,
          },
          {
            onSuccess: () => form.reset(),
            onSettled: () => setPending(false),
          },
        );
      }}
    >
      <h2 className="font-display text-2xl">{m.contact_form_title()}</h2>
      <p className="text-sm text-foreground/70">{m.contact_form_hint()}</p>

      {/* Honeypot: hidden from users, bots often fill it */}
      <div className="absolute -left-[9999px] top-auto h-0 w-0 overflow-hidden" aria-hidden="true">
        <Label htmlFor="contact-website">Website</Label>
        <Input id="contact-website" name="website" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="contact-name">{m.contact_label_name()}</Label>
        <Input
          id="contact-name"
          name="name"
          className="border-white/20 bg-black/20"
          required
          maxLength={120}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-email">{m.contact_email()}</Label>
        <Input
          id="contact-email"
          name="email"
          type="email"
          autoComplete="email"
          className="border-white/20 bg-black/20"
          required
          maxLength={255}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-phone">{m.contact_label_phone_opt()}</Label>
        <Input
          id="contact-phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          className="border-white/20 bg-black/20"
          maxLength={40}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-message">{m.contact_label_message()}</Label>
        <Textarea
          id="contact-message"
          name="message"
          rows={5}
          className="min-h-[120px] border-white/20 bg-black/20"
          required
          maxLength={5000}
        />
      </div>
      <Button
        type="submit"
        className="bg-ember-fill text-primary-foreground hover:bg-ember-fill/90"
        disabled={!hydrated || submitting}
      >
        {submitting ? m.contact_btn_sending() : m.contact_btn_send()}
      </Button>
    </form>
  );
}
